import { useTranslation } from 'react-i18next';
import { Box, Button } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { 
  TextFieldCustom,
  NumberField,
  DateField,
  SelectField 
} from "./CustomInputs";

const EmployeeForm = ({ initialValues, handleFormSubmit, buttonLabel }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  
  const checkoutSchema = yup.object().shape({
    role: yup.string().required(t('required_role')),
    name: yup.string().required(t('required_name')),
    phone: yup
      .string()
      .matches(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, t('invalid_phone'))
      .required(t('required_phone')),
    city: yup.string().when('role', {
      is: 'employee',
      then: yup.string().required(t('required_city'))
    }),
    salary: yup.number().when('role', {
      is: 'employee',
      then: yup.number().required(t('required_salary')).min(0)
    }),
    hire_date: yup.date().when('role', {
      is: 'employee',
      then: yup.date().required(t('required_hire_date'))
    }),
    companyName: yup.string().when('role', {
      is: 'supplier',
      then: yup.string().required(t('required_company'))
    }),
    customerCode: yup.string().when('role', {
      is: 'customer',
      then: yup.string().required(t('required_customer_code'))
    })
  });

  const defaultInitialValues = {
    role: 'employee',
    name: '',
    phone: '',
    city: '',
    salary: '',
    hire_date: '',
    companyName: '',
    customerCode: '',
    ...initialValues
  };

  return (
    <Formik
      initialValues={defaultInitialValues}
      validationSchema={checkoutSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            padding: { xs: '10px', sm: '20px' },
          }}
        >
          {/* Grid for Inputs */}
          <Box
            sx={{
              display: 'grid',
              gap: '20px',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
              },
            }}
          >
            {/* Role Selection */}
            <SelectField
              name="role"
              label={t('role')}
              formik={formik}
              options={[
                { value: 'employee', labelKey: 'employee' },
                { value: 'supplier', labelKey: 'supplier' },
                { value: 'customer', labelKey: 'customer' }
              ]}
              grid={{ xs: 1, sm: 1 }}
            />

            {/* Common Fields */}
            <TextFieldCustom 
              name="name"
              label={t('full_name')}
              formik={formik}
              grid={{ xs: 1, sm: 1 }}
              inputProps={{ dir: 'ltr' }}
            />

            <TextFieldCustom 
              name="phone"
              label={t('phone')}
              formik={formik}
              grid={{ xs: 1, sm: 1 }}
              inputProps={{ dir: 'ltr' }}
            />

            {/* Employee Specific Fields */}
            {formik.values.role === 'employee' && (
              <>
                <NumberField
                  name="salary"
                  label={t('salary')}
                  formik={formik}
                  grid={{ xs: 1, sm: 1 }}
                />

                <DateField
                  name="hire_date"
                  label={t('hire_date')}
                  formik={formik}
                  grid={{ xs: 1, sm: 1 }}
                />

                <TextFieldCustom
                  name="city"
                  label={t('city')}
                  formik={formik}
                  grid={{ xs: 1, sm: 1 }}
                />
              </>
            )}

            {/* Supplier Specific Field */}
            {formik.values.role === 'supplier' && (
              <TextFieldCustom
                name="companyName"
                label={t('company_name')}
                formik={formik}
                grid={{ xs: 1, sm: 1 }}
              />
            )}

            {/* Customer Specific Field */}
            {formik.values.role === 'customer' && (
              <TextFieldCustom
                name="customerCode"
                label={t('customer_code')}
                formik={formik}
                grid={{ xs: 1, sm: 1 }}
              />
            )}
          </Box>

          {/* Submit Button */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: { 
                xs: isRTL ? 'flex-start' : 'flex-end', 
                sm: isRTL ? 'flex-start' : 'flex-end' 
              }, 
              mt: { xs: '10px', sm: '20px' } 
            }}
          >
            <Button 
              type="submit" 
              color="secondary" 
              variant="contained"
              sx={{ 
                width: { xs: '100%', sm: 'auto' }, // Full width on mobile, auto on larger screens
                minWidth: { sm: '120px' },
                fontSize: { xs: '1rem', sm: '0.875rem' },
                px: { xs: 2, sm: 3 },
                py: { xs: 1, sm: 1.5 }
              }}
            >
              {buttonLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Formik>
  );
};

export default EmployeeForm;